import { useState } from "react";
import Layout from "@/components/feature/Layout";
import PageHero from "@/components/feature/PageHero";
import Button from "@/components/base/Button";
import { useSiteData } from "@/context/SiteDataContext";
import { useT } from "@/lib/useSiteText";
import type { SiteTextKey } from "@/lib/siteTexts";
import BodegaModal from "./BodegaModal";
import type { Bodega } from "@/types/content";

type TipoFiltro = "todas" | "mini" | "bodega" | "galpon";

const tipos: { valor: TipoFiltro; key: SiteTextKey }[] = [
  { valor: "todas", key: "bodegas.filtro_todas" },
  { valor: "mini", key: "bodegas.filtro_mini" },
  { valor: "bodega", key: "bodegas.filtro_bodega" },
  { valor: "galpon", key: "bodegas.filtro_galpon" },
];

export default function Bodegas() {
  const { data, whatsappLink } = useSiteData();
  const t = useT();
  const ctaDescripcion = t("bodegas.cta_descripcion");
  const bodegas = data.bodegas;
  const hero = data.pages.bodegas;
  const [tipo, setTipo] = useState<TipoFiltro>("todas");
  const [soloOficina, setSoloOficina] = useState(false);
  const [soloDisponible, setSoloDisponible] = useState(false);
  const [openBodega, setOpenBodega] = useState<Bodega | null>(null);

  const filtradas = bodegas.filter((b) => {
    if (tipo !== "todas" && b.tipo !== tipo) return false;
    if (soloOficina && !b.oficina) return false;
    if (soloDisponible && !b.disponible) return false;
    return true;
  });

  return (
    <Layout>
      <PageHero
        titulo={hero.hero_titulo}
        descripcion={hero.hero_descripcion}
        imagen={hero.hero_imagen_url}
        alt={hero.hero_imagen_alt}
      />

      <section className="bg-background-50">
        <div className="mx-auto max-w-7xl px-4 md:px-6 py-10 md:py-14">
          <div className="flex flex-col lg:flex-row lg:items-center gap-5 lg:justify-between">
            <div className="flex flex-wrap items-center gap-2">
              {tipos.map((tipoItem) => (
                <button
                  key={tipoItem.valor}
                  type="button"
                  onClick={() => setTipo(tipoItem.valor)}
                  className={`px-4 py-2 rounded-full text-sm font-semibold transition whitespace-nowrap cursor-pointer ${
                    tipo === tipoItem.valor
                      ? "bg-primary-500 text-background-50"
                      : "bg-background-100 text-foreground-700 hover:bg-background-200"
                  }`}
                >
                  {t(tipoItem.key)}
                </button>
              ))}
            </div>

            <div className="flex flex-wrap items-center gap-4">
              <label className="inline-flex items-center gap-2 text-sm text-foreground-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={soloOficina}
                  onChange={(e) => setSoloOficina(e.target.checked)}
                  className="w-4 h-4 accent-primary-500"
                />
                {t("bodegas.con_oficina")}
              </label>
              <label className="inline-flex items-center gap-2 text-sm text-foreground-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={soloDisponible}
                  onChange={(e) => setSoloDisponible(e.target.checked)}
                  className="w-4 h-4 accent-primary-500"
                />
                {t("bodegas.disponible_ahora")}
              </label>
            </div>
          </div>

          <p className="mt-6 text-sm text-foreground-500">
            {t("bodegas.mostrando", { n: filtradas.length, total: bodegas.length })}
          </p>

          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtradas.map((b) => (
              <div
                key={b.id}
                className="group rounded-lg bg-background-50 border border-background-200 overflow-hidden flex flex-col hover:-translate-y-1 transition-transform duration-300"
              >
                <button
                  type="button"
                  onClick={() => setOpenBodega(b)}
                  className="relative h-52 overflow-hidden block w-full cursor-pointer"
                >
                  <img
                    src={b.imagen_url}
                    alt={b.imagen_alt}
                    className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  />
                  <span
                    className={`absolute top-3 left-3 rounded-full px-3 py-1 text-xs font-semibold ${
                      b.disponible
                        ? "bg-primary-500 text-background-50"
                        : "bg-background-300 text-foreground-700"
                    }`}
                  >
                    {b.disponible ? t("bodegas.badge_disponible") : t("bodegas.badge_ocupado")}
                  </span>
                </button>

                <div className="p-5 flex flex-col flex-1">
                  <div className="flex items-start justify-between gap-3">
                    <button type="button" onClick={() => setOpenBodega(b)} className="text-left cursor-pointer">
                      <h2 className="font-heading font-semibold text-lg text-foreground-900 leading-snug hover:text-primary-600 transition">
                        {b.nombre}
                      </h2>
                    </button>
                    <span className="shrink-0 rounded-md bg-accent-100 text-accent-800 px-2.5 py-1 text-xs font-bold whitespace-nowrap">
                      {b.m2} m²
                    </span>
                  </div>

                  <p className="mt-1 text-sm text-foreground-500">
                    {t("bodegas.precio_desde")}{" "}
                    <strong className="text-foreground-800 font-semibold">UF {b.valor_uf.toLocaleString("es-CL")}</strong>{" "}
                    {t("bodegas.precio_sufijo")}
                  </p>

                  <div className="mt-4 flex flex-wrap gap-2">
                    {b.caracteristicas.slice(0, 3).map((c) => (
                      <span
                        key={c}
                        className="text-xs rounded-full bg-secondary-100 text-secondary-900 px-2.5 py-1 whitespace-nowrap"
                      >
                        {c}
                      </span>
                    ))}
                  </div>

                  <div className="mt-auto pt-4 border-t border-background-200 flex items-center justify-between gap-3">
                    <button
                      type="button"
                      onClick={() => setOpenBodega(b)}
                      className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary-600 hover:text-primary-700 transition cursor-pointer"
                    >
                      {t("bodegas.ver_detalle")} <i className="ri-arrow-right-line" />
                    </button>
                    <a
                      href={whatsappLink(t("wa.cotizar_bodega", { nombre: b.nombre }))}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-2 text-sm font-semibold text-accent-600 hover:text-accent-700 transition"
                    >
                      <i className="ri-whatsapp-line" /> {t("bodegas.consultar")}
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {filtradas.length === 0 && (
            <div className="mt-10 text-center py-16 rounded-lg bg-background-100">
              <p className="text-foreground-600">{t("bodegas.vacio")}</p>
              <button
                type="button"
                onClick={() => {
                  setTipo("todas");
                  setSoloOficina(false);
                  setSoloDisponible(false);
                }}
                className="mt-3 text-sm font-semibold text-primary-600 hover:text-primary-700 cursor-pointer"
              >
                {t("bodegas.limpiar_filtros")}
              </button>
            </div>
          )}

          <div className="mt-12 rounded-lg bg-primary-800 p-8 md:p-10 text-center">
            <h2 className="font-heading font-bold text-xl md:text-2xl text-background-50">
              {t("bodegas.cta_titulo")}
            </h2>
            {ctaDescripcion && <p className="mt-2 text-background-200">{ctaDescripcion}</p>}
            <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
              <Button href={whatsappLink()} variant="accent" icon="ri-whatsapp-line">
                {t("bodegas.cta_boton_whatsapp")}
              </Button>
              <Button to="/contacto" className="bg-background-50 text-primary-800 hover:bg-background-100">
                {t("bodegas.cta_boton_visita")}
              </Button>
            </div>
          </div>
        </div>
      </section>

      <BodegaModal bodega={openBodega} onClose={() => setOpenBodega(null)} whatsappLink={whatsappLink} />
    </Layout>
  );
}